import { useCallback, useEffect, useRef } from 'react';

/**
 * Хук для безопасной работы с таймерами
 * Автоматически очищает все таймеры при размонтировании компонента
 * 
 * @example
 * const { safeSetTimeout, safeSetInterval, clearAllTimers } = useTimerCleanup();
 * 
 * // Использование вместо обычного setTimeout
 * safeSetTimeout(() => {
 *   console.log('Выполнится через 1 секунду');
 * }, 1000);
 * 
 * // При размонтировании компонента таймер автоматически очистится
 */
export const useTimerCleanup = () => {
    const timeoutIds = useRef<Set<NodeJS.Timeout>>(new Set());
    const intervalIds = useRef<Set<NodeJS.Timeout>>(new Set());
    const isMounted = useRef(true);

    // Очистка всех таймеров при размонтировании
    useEffect(() => {
        isMounted.current = true;
        
        return () => {
            isMounted.current = false;
            
            // Очищаем все timeout
            timeoutIds.current.forEach((id) => {
                clearTimeout(id);
            });
            timeoutIds.current.clear();
            
            // Очищаем все interval
            intervalIds.current.forEach((id) => {
                clearInterval(id);
            });
            intervalIds.current.clear();
        };
    }, []);

    /**
     * Безопасный setTimeout с автоматической очисткой
     */
    const safeSetTimeout = useCallback((callback: () => void, delay: number): NodeJS.Timeout => {
        const id = setTimeout(() => {
            timeoutIds.current.delete(id);
            if (isMounted.current) {
                callback();
            }
        }, delay);
        
        timeoutIds.current.add(id);
        return id;
    }, []);

    /**
     * Безопасный setInterval с автоматической очисткой
     */
    const safeSetInterval = useCallback((callback: () => void, delay: number): NodeJS.Timeout => {
        const id = setInterval(() => {
            if (isMounted.current) {
                callback();
            }
        }, delay);
        
        intervalIds.current.add(id);
        return id;
    }, []);

    /**
     * Очистить конкретный timeout
     */
    const safeClearTimeout = useCallback((id: NodeJS.Timeout) => {
        clearTimeout(id);
        timeoutIds.current.delete(id);
    }, []);

    /**
     * Очистить конкретный interval
     */
    const safeClearInterval = useCallback((id: NodeJS.Timeout) => {
        clearInterval(id);
        intervalIds.current.delete(id);
    }, []);

    /**
     * Очистить все таймеры вручную
     */
    const clearAllTimers = useCallback(() => {
        timeoutIds.current.forEach((id) => {
            clearTimeout(id);
        });
        timeoutIds.current.clear();
        
        intervalIds.current.forEach((id) => {
            clearInterval(id);
        });
        intervalIds.current.clear();
    }, []);

    /**
     * Проверить, смонтирован ли компонент
     */
    const getIsMounted = useCallback(() => isMounted.current, []);

    return {
        safeSetTimeout,
        safeSetInterval,
        safeClearTimeout,
        safeClearInterval,
        clearAllTimers,
        getIsMounted,
    };
};

export default useTimerCleanup;
